import { useEffect, useState } from 'react';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';

/**
 * A length in words: "1 hr 30 min", or "45 min" under the hour.
 *
 * Its own function because the sub-hour case was being got wrong: the field
 * read 45 minutes back as "0 hr 45 min", which is nobody's way of saying it
 * and the floor is 15 minutes, so it was reachable.
 */
export function readableHours(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;

  return `${Math.floor(minutes / 60)} hr${minutes % 60 ? ` ${minutes % 60} min` : ''}`;
}

/**
 * How long a day is, in hours, entered rather than chosen.
 *
 * This was a dropdown of eight fixed slots, which is fine until somebody has
 * four and a half hours — and then there is simply no way to say so. A number
 * accepts any of them.
 *
 * Held as text while it is being typed so the box can be empty mid-edit
 * rather than snapping to a value, and so "4." on the way to "4.5" is not
 * rewritten under the cursor. The value only leaves here when it parses to
 * something the API will accept.
 *
 * There is no "unset" state. The length is only ever asked for where it does
 * something — in the kinds mode, with at least one kind of site chosen — and
 * in that position it always has an answer, so a control offering to take it
 * away would be offering to make the section meaningless.
 */
export function DurationField({
  minutes, onChange, label = 'Time on task', helperText, purpose, sx,
}: {
  minutes: number;
  onChange: (minutes: number) => void;
  label?: string;
  helperText?: string;
  /**
   * What the number is FOR, said above the field.
   *
   * Above rather than below, because the line under it is already taken by
   * the live reading of what was typed — "4.5" answering back "4 hr 30 min"
   * is worth more than a restatement of the label, and losing it to make
   * room for an explanation would trade a working control for a described
   * one. Omitted where the surrounding text has already said it.
   */
  purpose?: string;
  sx?: object;
}) {
  const [text, setText] = useState(() => String(minutes / 60));

  useEffect(() => { setText(String(minutes / 60)); }, [minutes]);

  const commit = () => {
    const hours = Number(text);
    if (!Number.isFinite(hours)) return setText(String(minutes / 60));

    /*
     * Rounded to a quarter of an hour, and clamped to what the API takes
     * (15 minutes to 24 hours). Somebody typing 3.7 means "about three and
     * three quarters", and a schedule printed to the minute from it would be
     * a precision nobody asked for.
     */
    const rounded = Math.round((hours * 60) / 15) * 15;
    const clamped = Math.min(24 * 60, Math.max(15, rounded));
    onChange(clamped);
    setText(String(clamped / 60));
  };

  const asMinutes = Math.round(Number(text) * 60);
  const readable = Number.isFinite(asMinutes) && asMinutes > 0 ? readableHours(asMinutes) : '';

  const field = (
    <TextField
      size="small"
      type="number"
      label={label}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      helperText={helperText ?? readable}
      slotProps={{
        htmlInput: { min: 0.25, max: 24, step: 0.5, 'aria-label': label },
        input: { endAdornment: <InputAdornment position="end">hours</InputAdornment> },
      }}
      sx={purpose ? { width: '100%' } : (sx ?? {})}
    />
  );

  if (!purpose) return field;

  return (
    <Box sx={sx ?? {}}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {purpose}
      </Typography>
      {field}
    </Box>
  );
}
